/**
 * Zustellprotokoll der Rueckkanal-Webhooks je Quelle (Spec 5.3).
 *
 * Zusammen mit der Ansicht der fehlgeschlagenen Jobs **ist** das die DLQ
 * (CLAUDE.md, Override „DLQ") — deshalb hat jede nicht zugestellte Zeile hier
 * einen Retry-Knopf und keinen Umweg ueber Redis.
 */
import type { WebhookDelivery } from '@tickethub/shared';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export type WebhookDeliveriesProps = {
  deliveries: WebhookDelivery[] | null;
  busy: boolean;
  onRetry: (deliveryId: string) => Promise<void>;
};

const VARIANT: Record<WebhookDelivery['status'], 'secondary' | 'outline' | 'destructive'> = {
  delivered: 'secondary',
  pending: 'outline',
  failed: 'destructive',
};

function formatTime(value: string | null, locale: string): string {
  return value === null ? '—' : new Date(value).toLocaleString(locale);
}

export function WebhookDeliveries({ deliveries, busy, onRetry }: WebhookDeliveriesProps) {
  const { t, i18n } = useTranslation();

  if (deliveries === null) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (deliveries.length === 0) {
    return (
      <Alert>
        <AlertDescription>{t('webhook.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <ul className="space-y-2">
      {deliveries.map((delivery) => (
        <li key={delivery.id} className="space-y-1 rounded-lg border p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={VARIANT[delivery.status]}>{t(`webhook.status_${delivery.status}`)}</Badge>
            <span className="font-mono text-xs">{delivery.event}</span>
            <span className="text-xs text-muted-foreground">
              {formatTime(delivery.created_at, i18n.language)}
            </span>
            {delivery.status === 'delivered' ? null : (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={busy}
                onClick={() => void onRetry(delivery.id)}
              >
                <RefreshCw aria-hidden="true" />
                {t('webhook.retry')}
              </Button>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-4">
            <div>
              <dt className="inline">{t('webhook.attempt')}: </dt>
              <dd className="inline">{delivery.attempt}</dd>
            </div>
            <div>
              <dt className="inline">{t('webhook.response_code')}: </dt>
              <dd className="inline">{delivery.response_code ?? '—'}</dd>
            </div>
            <div>
              <dt className="inline">{t('webhook.next_retry')}: </dt>
              <dd className="inline">{formatTime(delivery.next_retry_at, i18n.language)}</dd>
            </div>
            <div>
              <dt className="inline">{t('webhook.delivered_at')}: </dt>
              <dd className="inline">{formatTime(delivery.delivered_at, i18n.language)}</dd>
            </div>
          </dl>

          {delivery.error ? (
            <p className="break-all font-mono text-xs text-destructive">{delivery.error}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
